import { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Hook to calculate the total cost for a location on a custom plan.
 * Takes in custom pricing details (fetched from API) and location details,
 * and returns the total monthly cost.
 * @param {number} pickupBasePrice charge per pickup per month
 * @param {number} additionalFloorPrice charge to add per pickup for each floor @ the location > 1
 * @param {number} additionalBuildingPrice charge to add per pickup for each building > 1
 * @param {number} numMonthlyPickups how many pickups selected for this location per month
 * @param {number} numAdditionalFloors how many floors > 1 selected at this location
 * @param {number} numAdditionalBuildings how many buildings > 1 selected at this location
 * @returns total cost for this location per month
 */
export const useCustomPricing = (
  { basePickupPrice, additionalFloorPrice, additionalBuildingPrice },
  numMonthlyPickups,
  numAdditionalFloors,
  numAdditionalBuildings,
) => {
  // price added for additional floors
  const floorsPrice = useMemo(
    () => (numAdditionalFloors) * additionalFloorPrice,
    [numAdditionalFloors, additionalFloorPrice],
  );

  // price added for additional buildings
  const buildingsPrice = useMemo(
    () => (numAdditionalBuildings) * additionalBuildingPrice,
    [numAdditionalBuildings, additionalBuildingPrice],
  );

  const pricePerPickup = useMemo(
    () => basePickupPrice + floorsPrice + buildingsPrice,
    [basePickupPrice, floorsPrice, buildingsPrice],
  );

  // total price for the location
  const totalPrice = useMemo(
    () => numMonthlyPickups * pricePerPickup,
    [numMonthlyPickups, pricePerPickup],
  );

  return {
    totalPrice,
    floorsPrice,
    buildingsPrice,
    pricePerPickup,
  };
};

export const CustomPricingPropType = PropTypes.shape({
  pickupBasePrice: PropTypes.number,
  additionalFloorPrice: PropTypes.number,
  additionalBuildingPrice: PropTypes.number,
});
